<template>
  <v-container fluid>
    <!-- Loading -->
    <div v-if="loading" class="text-center pa-10">
      <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
      <div class="mt-3 grey--text">กำลังโหลดข้อมูล...</div>
    </div>

    <div v-else-if="docket">
      <!-- ===== BREADCRUMB ===== -->
      <div class="caption grey--text mb-2">
        Incorporation &amp; Corporate &rsaquo; <strong>Docket Detail</strong>
      </div>

      <!-- ===== HEADER ===== -->
      <v-row align="center" class="mb-4">
        <v-col>
          <div class="text-h5 font-weight-bold">
            Docket: {{ docket.inquiry_topic || "-" }}
          </div>
          <div class="caption grey--text d-flex align-center flex-wrap" style="gap:8px;">
            <span>{{ docket.client_name }}</span>
            <span v-if="docket.doc_category">
              &nbsp;|&nbsp; {{ docket.doc_category }}
            </span>
            <v-chip :color="getDocketStatusColor(docket.status)" :text-color="getDocketStatusTextColor(docket.status)"
              x-small label>
              <v-icon left x-small>mdi-circle</v-icon>{{ docket.status }}
            </v-chip>
            <!-- ⭐ แสดง invoice_id ดิบไปก่อน — ยังไม่ join เลขที่ใบแจ้งหนี้จริงจาก inv_h -->
            <v-chip v-if="docket.invoice_id" x-small label outlined color="blue darken-2">
              <v-icon left x-small>mdi-receipt-text-outline</v-icon>Invoice #{{
                docket.invoice_id
              }}
            </v-chip>
          </div>
        </v-col>
        <v-col class="d-flex justify-end" style="gap:8px;">
          <v-btn outlined small @click="$router.push('/incorporate-docket-list')">
            <v-icon left small>mdi-arrow-left</v-icon>กลับ
          </v-btn>
          <v-btn outlined small color="orange darken-2" @click="goToEdit" v-if="authorize_edit">
            <v-icon left small>mdi-pencil</v-icon>Edit
          </v-btn>
        </v-col>
      </v-row>

      <!-- ===== DATES ===== -->
      <v-card outlined class="mb-4">
        <v-card-text>
          <v-row>
            <v-col cols="12" md="4">
              <div class="field-label">Date Received</div>
              <div class="field-value">
                {{ formatDate(docket.date_received) }}
              </div>
            </v-col>
            <v-col cols="12" md="4">
              <div class="field-label">Date Submitted</div>
              <div class="field-value">
                {{ formatDate(docket.date_submitted) }}
              </div>
            </v-col>
            <v-col cols="12" md="4">
              <div class="field-label">Next Due Date</div>
              <div class="field-value">
                {{ formatDate(docket.next_due_date) }}
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- ===== DOCUMENT CATEGORY ===== -->
      <v-card outlined class="mb-4" v-if="docCategoryList.length">
        <div class="section-header">📁 DOCUMENT CATEGORY | ชนิดเอกสาร</div>
        <v-card-text>
          <v-chip v-for="(cat, i) in docCategoryList" :key="i" small label class="mr-2 mb-2">{{ cat }}</v-chip>
        </v-card-text>
      </v-card>

      <!-- ===== CHANGE TRACKER ===== -->
      <v-card outlined class="mb-4" v-if="changeTrackerList.length">
        <div class="section-header">🔄 CHANGE TRACKER | การเปลี่ยนแปลง</div>
        <v-card-text>
          <v-chip v-for="(tr, i) in changeTrackerList" :key="i" small label color="pink lighten-4" class="mr-2 mb-2">{{
            tr }}</v-chip>
        </v-card-text>
      </v-card>

      <!-- ===== REMARK ===== -->
      <v-card outlined class="mb-4" v-if="docket.remark">
        <div class="section-header">💬 REMARK</div>
        <v-card-text>
          <div class="field-value" style="white-space: pre-line;">
            {{ docket.remark }}
          </div>
        </v-card-text>
      </v-card>

      <!-- ===== ATTACHED DOCUMENTS ===== -->
      <v-card outlined class="mb-4">
        <div class="section-header">📎 ATTACHED DOCUMENTS</div>
        <v-card-text>
          <div v-if="attachmentList.length === 0" class="caption grey--text">
            ไม่มีไฟล์เอกสารที่แนบ
          </div>
          <div v-for="(file, i) in attachmentList" :key="i" class="d-flex align-center attachment-row mb-2">
            <v-icon left color="grey darken-1">mdi-file-document-outline</v-icon>
            <span class="caption">{{ file }}</span>
            <v-spacer></v-spacer>
            <v-btn outlined x-small :href="`${fileIncorporateUrl}/${docket.id}/${file}`" target="_blank">
              <v-icon left x-small>mdi-download</v-icon>Download
            </v-btn>
          </div>
        </v-card-text>
      </v-card>

      <!-- ===== TIMELINE ===== -->
      <!-- ⭐ เพิ่มใหม่ — แสดงประวัติ Timeline ของ docket นี้แบบ read-only
           ใช้ TimeLineOperations component เดิม (ตัวเดียวกับที่ใช้ในหน้า
           Add/Edit) แต่ปิดการ์ด input (Status/Status Note/Client Visibility)
           ด้วย showStatusInputCard=false เพราะหน้า View เป็นหน้าดูข้อมูล
           อย่างเดียว ไม่ควรแก้ไข status ตรงนี้ (ต้องไปกดปุ่ม Edit แทน)
           section-header ใช้สไตล์เดียวกับ section อื่น ๆ ในหน้านี้ -->
      <v-card outlined class="mb-4">
        <div class="section-header"> 💰 E · การเงิน &amp; สถานะ Invoice</div>
        <v-card-text>

          <div class="invoice-box">
            <v-row>
              <v-col cols="12" md="4">
                <div class="field-label">เลขที่ใบเสนอราคา / Quotation ID</div>
                <v-autocomplete v-model="formQutations.salesOrderId" :items="salesOrderList" item-value="quotation_id"
                  item-text="doc_no" placeholder="SO-69-0001" outlined dense hide-details="auto"
                  background-color="#f2f2f2" clearable @change="onSelectSO" disabled />
              </v-col>
              <v-col cols="12" md="4">
                <div class="field-label">Price</div>
                <v-text-field v-model="formQutations.price" type="number" outlined dense hide-details="auto"
                  placeholder="฿" background-color="#f2f2f2" disabled />
              </v-col>

            </v-row>
            <v-row v-if="modeOperations == 'New' || (modeOperations == 'Edit' && invoiceList.length === 0)">
              <v-col cols="12" md="3">
                <div class="field-label">
                  เลขที่ Invoice
                  <v-tooltip top max-width="260">
                    <template #activator="{ on, attrs }">
                      <span class="info-tag" v-bind="attrs" v-on="on">i</span>
                    </template>
                    <span>แนะนำเพิ่ม: บันทึกเลขบิลเมื่อออกแล้ว เพื่อตรวจสอบย้อนหลัง</span>
                  </v-tooltip>

                </div>
                <v-text-field placeholder="INV-… (auto เมื่อออก)" disabled outlined dense hide-details="auto"
                  background-color="#f2f2f2" />
              </v-col>
              <v-col cols="12" md="3">
                <div class="field-label">
                  วันที่ออก Invoice
                </div>
                <!-- <v-text-field v-model="form.invoiceDate" type="date" outlined dense hide-details="auto"
                                    background-color="white" /> -->
                <v-menu v-model="invoiceMenu" :close-on-content-click="false" transition="scale-transition" offset-y
                  min-width="auto" disabled>
                  <template #activator="{ on, attrs }">
                    <v-text-field placeholder="dd/mm/yyyy" readonly outlined dense hide-details="auto" v-bind="attrs"
                      v-on="on" append-icon="mdi-calendar" background-color="#f2f2f2" />
                  </template>
                  <v-date-picker v-model="formQutations.invoiceDate" no-title locale="th"
                    @input="invoiceMenu = false" />
                </v-menu>
              </v-col>
              <v-col cols="12" md="3">
                <div class="field-label">
                  วันครบกำหนดชำระ
                </div>
                <!-- <v-text-field v-model="form.dueDate" type="date" outlined dense hide-details="auto"
                                    background-color="white" /> -->
                <v-menu v-model="invoiceMenuPay" :close-on-content-click="false" transition="scale-transition" offset-y
                  min-width="auto" disabled>
                  <template #activator="{ on, attrs }">
                    <v-text-field placeholder="dd/mm/yyyy" readonly outlined dense hide-details="auto" v-bind="attrs"
                      v-on="on" append-icon="mdi-calendar" background-color="#f2f2f2" />
                  </template>
                  <v-date-picker v-model="formQutations.dueDate" no-title locale="th" @input="invoiceMenuPay = false" />
                </v-menu>
              </v-col>
              <v-col cols="12" md="3">
                <div class="field-label">
                  Invoice Status
                  <!-- <v-tooltip top max-width="260">
                    <template #activator="{ on, attrs }">
                      <span class="info-tag" v-bind="attrs" v-on="on">i</span>
                    </template>
                    <span>หัวใจของโมดูล: ติดตามรอบบิลแบบเดียวกับ Immigration</span>
                  </v-tooltip> -->

                </div>
                <v-select placeholder="auto เมื่อออก" :items="invoiceStatusList" outlined dense hide-details="auto"
                  background-color="#f2f2f2" disabled />
              </v-col>
            </v-row>
            <template v-if="modeOperations == 'Edit' && invoiceList.length > 0">
              <v-row v-for="(item, index) in invoiceList" :key="item.id">
                <v-col cols="12" md="3">
                  <div class="field-label">
                    เลขที่ Invoice
                    <v-tooltip top max-width="260">
                      <template #activator="{ on, attrs }">
                        <span class="info-tag" v-bind="attrs" v-on="on">i</span>
                      </template>
                      <span>แนะนำเพิ่ม: บันทึกเลขบิลเมื่อออกแล้ว เพื่อตรวจสอบย้อนหลัง</span>
                    </v-tooltip>
                  </div>
                  <v-text-field :value="item.invoice_display" placeholder="INV-… (auto เมื่อออก)" disabled outlined
                    dense hide-details="auto" background-color="#f2f2f2" />
                </v-col>
                <v-col cols="12" md="3">
                  <div class="field-label">วันที่ออก Invoice</div>
                  <v-text-field :value="item.created_at" disabled placeholder="dd/mm/yyyy" readonly outlined dense
                    hide-details="auto" append-icon="mdi-calendar" background-color="#f2f2f2" />
                </v-col>
                <v-col cols="12" md="3">
                  <div class="field-label">วันครบกำหนดชำระ</div>
                  <v-text-field :value="item.inv_date" disabled placeholder="dd/mm/yyyy" readonly outlined dense
                    hide-details="auto" append-icon="mdi-calendar" background-color="#f2f2f2" />
                </v-col>
                <v-col cols="12" md="2">
                  <div class="field-label">
                    Invoice Status
                    <v-tooltip top max-width="260">
                      <template #activator="{ on, attrs }">
                        <span class="info-tag" v-bind="attrs" v-on="on">i</span>
                      </template>
                      <span>หัวใจของโมดูล: ติดตามรอบบิลแบบเดียวกับ Immigration</span>
                    </v-tooltip>
                  </div>
                  <v-text-field :value="item.invoice_status" placeholder="auto เมื่อออก" outlined dense
                    hide-details="auto" background-color="#f2f2f2" disabled />
                </v-col>
                <v-col cols="12" md="1" class="d-flex align-center justify-center" style="margin-top: 24px;">
                  <v-tooltip top>
                    <template #activator="{ on, attrs }">
                      <v-btn icon color="primary" v-bind="attrs" v-on="on" @click="previewInvoice(item)">
                        <v-icon>mdi-eye</v-icon>
                      </v-btn>
                    </template>
                    <span>ดูรายละเอียด</span>
                  </v-tooltip>
                </v-col>
              </v-row>
            </template>
          </div>
        </v-card-text>
      </v-card>

      <v-card outlined class="mb-4">
        <div class="section-header">🕒 TIMELINE | ประวัติการดำเนินงาน</div>
        <v-card-text>
          <div v-if="loadingTimeline" class="text-center pa-6">
            <v-progress-circular indeterminate color="primary" size="30"></v-progress-circular>
          </div>
          <TimeLineOperations v-else :showaddItem="timelineForm" :statuslist="statuslist" :reportlist="reportlist"
            :modeOperations="'Edit'" :timelineStatus="0" :status_note="''" :visible_check="false"
            :module_name="module_name" :timeline_case_id="Number(docket.id)" :showReportInv="false"
            :showStatusInputCard="false" />
        </v-card-text>
      </v-card>
    </div>

    <!-- Not found -->
    <div v-else class="text-center pa-10 grey--text">
      ไม่พบข้อมูล Docket นี้
    </div>
  </v-container>
</template>

<script>
import api from "@/services/api";
import { server } from "@/services/constants";
import TimeLineOperations from "../timeline/TimeLineOperations.vue"; // ⭐ เพิ่มใหม่

// ✅ ไฟล์ incorporate อยู่ที่ production storage port 3001
const INCORPORATE_DOCKET_FILE_URL = process.env.VUE_APP_PRODUCTION_STORAGE_URL
  ? `${process.env.VUE_APP_PRODUCTION_STORAGE_URL}/incorporatedocket`
  : "http://localhost:3001/incorporatedocket";

import { getIncorporateDocketById } from "@/services/api-dtime/api_incorporate";

export default {
  name: "incorporate-docket-view",
  components: { TimeLineOperations }, // ⭐ เพิ่มใหม่
  data() {
    return {
      loading: true,
      docket: null,
      fileIncorporateUrl: INCORPORATE_DOCKET_FILE_URL,

      authorize_view: false,
      authorize_add: false,
      authorize_edit: false,
      authorize_del: false,
      authorize_id: "",
      userId: "",

      // ⭐ เพิ่มใหม่ทั้งหมด — สำหรับส่วน Timeline (read-only)
      loadingTimeline: true,
      module_name: "Corporate",
      statuslist: [],
      reportlist: [],
      // ⭐ TimeLineOperations ต้องการ prop "showaddItem" เป็น Object เสมอ
      // (ใช้แสดง Reported/Invoiced เมื่อ showReportInv=true) — หน้านี้ปิด
      // showReportInv ไว้อยู่แล้ว จึงส่ง object เปล่าไปพอ ไม่กระทบอะไร
      timelineForm: {},
      //quotations
      formQutations: {
        salesOrderId: 0,
        price: 0
      },
      salesOrderList: [],
      invoiceList: [],
      invoiceMenu: false,
      invoiceMenuPay: false,
      invoiceStatusList: ["Draft", "Requested", "Invoiced", "Paid"],
      quotationscheckstatus: '',
      modeOperations: 'Edit'
    };
  },
  computed: {
    docCategoryList() {
      if (!this.docket?.doc_category) return [];
      return this.docket.doc_category
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    },
    // ⭐ ใช้ docket.changes (จากตาราง incorporate_docket_change) เป็นหลัก
    // ถ้า backend ยังไม่ส่ง changes มา (เช่น docket เก่า) จะ fallback ไปอ่าน
    // change_tracker text เดิมแทน เพื่อไม่ให้หน้าจอพังกับข้อมูลเก่า
    changeTrackerList() {
      if (
        Array.isArray(this.docket?.changes) &&
        this.docket.changes.length > 0
      ) {
        return this.docket.changes.map((c) =>
          this.changeTypeLabel(c.change_type),
        );
      }
      if (!this.docket?.change_tracker) return [];
      return this.docket.change_tracker
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    },
    attachmentList() {
      if (!this.docket?.file_attachments) return [];
      return this.docket.file_attachments
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    },
  },
  async beforeCreate() {
    this.$store.state.navMenu = true;
  },
  async mounted() {
    await this.loadDataInvoice(this.$route.params.id);
    this.$hideLoader();
    this.userId = localStorage.getItem(server.USER_ID);
    this.authorize_id = localStorage.getItem(server.AUTHORIZE_ID);

    let parser = document.createElement("a");
    parser.href = window.location;
    const router_path = "incorporate-docket-list"; // ใช้ permission เดียวกับ list
    try {
      const { default: api } = await import("@/services/api");
      const res_auth = await api.getAuthorize(this.userId, router_path);
      this.authorize_view = res_auth.data[0].smd_view >= 1;
      this.authorize_add = res_auth.data[0].smd_add >= 1;
      this.authorize_edit = res_auth.data[0].smd_edit >= 1;
      this.authorize_del = res_auth.data[0].smd_del >= 1;
    } catch (error) {
      console.error("Error checking authorize:", error);
    }

    const id = this.$route.params.id;
    await this.loadDocket(id); // เดิม ไม่แตะ
    this.loading = false; // เดิม ไม่แตะ

    // ⭐ เพิ่ม — โหลด statuslist สำหรับส่วน Timeline หลังจากโหลด docket
    // เสร็จแล้ว (ไม่ block การแสดงข้อมูลหลักของหน้า ให้ section Timeline
    // โชว์ loading spinner ของตัวเองแยกต่างหาก ผ่าน loadingTimeline)
    await this.loaddataLitigationCourtStatus();
    this.loadingTimeline = false;
  },
  methods: {
    previewInvoice(item) {
      const routeData = this.$router.resolve(
        `/edit-invoice/${item.inv_id_preview}/inv_h/edit`
      );
      window.open(routeData.href, '_blank');
    },
    //quotations 
    async loaddataSalesOrder() {
      try {
        const res = await api.getQuotatinsForDocketCore(Number(localStorage.getItem(server.COMPANYID)));
        this.salesOrderList = res.data || [];

      } catch (error) {
        console.error("loaddataSalesOrder error:", error);
      }
    },
    onSelectSO(value) {
      if (!value) {
        this.formQutations.price = "";
        return
      }
      // หา object ของ SO ที่เลือก
      const so = this.salesOrderList.find(item => item.quotation_id === value);
      if (so) {
        this.quotationscheckstatus = so.status;
        this.formQutations.price = Number(so.subtotal);
      }
    },
    async loadDataInvoice(id) {
      try {
        var dataserach = {
          module_name: 'Incorporation',
          module_id: id,
          company_id: Number(localStorage.getItem(server.COMPANYID)),
        }
        const res = await api.getQuotatinsinvoiceListCore({
          params: dataserach
        })
        this.invoiceList = res.data.data || [];
      } catch (error) {
        console.error("loadDataInvoice error:", error);
      }
    },
    //end quotattions
    async loadDocket(id) {
      try {
        const res = await getIncorporateDocketById(id);
        this.docket = res.data;
      } catch (error) {
        console.error("Error loading docket:", error);
      }
    },

    // ⭐ เพิ่มใหม่ — โหลด config สถานะ (client_timeline_status_config)
    // จำเป็นสำหรับ TimeLineOperations แม้จะปิดการ์ด input ไว้ก็ตาม เพราะ
    // ตัว component เองต้องใช้ hydration บางส่วน (ไม่ได้ error ถ้าไม่ส่งมา
    // แต่ปลอดภัยกว่าถ้าโหลดให้ครบตาม pattern เดียวกับหน้า Add/Edit/List)
    async loaddataLitigationCourtStatus() {
      try {
        const { default: api } = await import("@/services/api");
        const result = await api.getClientTimelineStatusCongFigByModule(
          "Corporate",
        );
        this.statuslist = (result.data || []).map((item) => ({
          ...item,
          module_id: item.module_id,
        }));
      } catch (error) {
        console.error("Error loading status config:", error);
      }
    },

    // ⭐ แปลง change_type (key) ให้เป็น label อ่านง่ายในหน้า View
    changeTypeLabel(key) {
      const map = {
        shareholders: "👥 Shareholders | ผู้ถือหุ้น",
        directors: "👔 Directors | กรรมการบริษัท",
        "reg-capital": "💰 Registered Capital (THB)",
        "paidup-capital": "💵 Paid-up Capital (THB)",
        "company-name": "🏢 Company Name | ชื่อบริษัท",
        "reg-address": "📍 Registered Address (Head Office)",
        auditor: "📋 Auditor | ผู้สอบบัญชี",
        accountant: "📊 Accountant | ผู้ทำบัญชี",
        others: "📝 Others | อื่น ๆ",
      };
      return map[key] || key;
    },

    goToEdit() {
      this.$router.push(`/incorporate-docket-edit/${this.docket.id}`);
    },

    formatDate(dateStr) {
      if (!dateStr) return "-";
      try {
        const d = new Date(dateStr);
        return d.toLocaleDateString("th-TH", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        });
      } catch {
        return dateStr;
      }
    },

    getDocketStatusColor(status) {
      const map = {
        "In Progress": "blue lighten-4",
        Complete: "green lighten-4",
        "Request Payment": "pink lighten-4",
        Cancelled: "red lighten-4",
      };
      return map[status] || "grey lighten-3";
    },
    getDocketStatusTextColor(status) {
      const map = {
        "In Progress": "blue darken-2",
        Complete: "green darken-2",
        "Request Payment": "pink darken-2",
        Cancelled: "red darken-2",
      };
      return map[status] || "grey darken-1";
    },
  },
};
</script>

<style scoped>
.section-header {
  font-size: 13px;
  font-weight: 700;
  color: #374151;
  border-left: 4px solid #0a7ea5;
  padding: 10px 16px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.field-label {
  font-size: 11px;
  color: #9ca3af;
  margin-bottom: 2px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.field-value {
  font-size: 14px;
  color: #111827;
  font-weight: 500;
}

.attachment-row {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 8px 12px;
}

.sec-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #1b7a99;
  font-weight: 700;
  margin-bottom: 14px;
}

.sec-label .hint {
  color: #6b7785;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
}

.invoice-box {
  background: #f7fafc;
  border: 1px solid #d8e6f0;
  border-radius: 8px;
  padding: 16px;
}

.field-label {
  font-size: 12.5px;
  color: #37424f;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 18px;
}

.field-label .hint {
  font-size: 11px;
  color: #6b7785;
}

.field-label .hint.danger {
  color: #c0392b;
}

.field-label .req {
  color: #c0392b;
}

.info-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #c47f00;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  cursor: help;
}
</style>